<template>
  <div>
    <UContainer>
      <div class="grid grid-cols-12 gap-3 mt-6 mb-4">
        <div class="col-span-12 lg:col-span-9">
          <div
            class="relative rounded-lg border-[1px] mb-6 border-neutral-200 dark:border-neutral-800"
          >
            <img
              src="https://picsum.photos/1600/900"
              height="200"
              class="h-[200px] w-full object-cover rounded-t-lg"
            />
            <div
              class="absolute top-40 left-8 border-4 border-white rounded-full dark:border-neutral-900"
            >
              <UAvatar
                :src="userDetail.imageUrl"
                :alt="userDetail.username"
                width="80"
                height="80"
                loading="lazy"
                :ui="{
                  image: 'w-[80px] h-[80px]',
                  root: 'w-[80px] h-[80px]',
                }"
              />
            </div>

            <div class="mt-16 mb-4 mx-8 flex-col md:flex-row flex justify-between">
              <div>
                <h1 class="text-3xl font-black">
                  {{ `${userDetail.firstName} ${userDetail.lastName}` }}
                </h1>
                <h2 class="text-neutral-400">{{ userDetail.username }}</h2>
                <span class="text-sm text-secondary-500 mt-1 font-medium"
                  >{{ userDetail.follows }} {{ LL.user_followers() }}</span
                >
              </div>
              <div class="flex self-start gap-2 mt-2">
                <UButton
                  v-if="router.params.username === userStore.username"
                  icon="i-heroicons-pencil-square"
                  color="neutral"
                  variant="subtle"
                  size="lg"
                  to="/users/me"
                >
                  {{ LL.update_profile() }}
                </UButton>
                <LazyUserFollow :user="userDetail" />
              </div>
            </div>
          </div>
          <div class="flex flex-col gap-6">
            <div>
              <p id="list-posts" class="text-2xl font-black leading-8 mb-4 mt-1">
                {{ LL.article() }}
              </p>
              <div>
                <div v-if="!isLoadingPost" class="flex flex-col">
                  <PostCard v-for="post in posts" :key="post.id" :post="post" />
                </div>
                <div v-if="isLoadingPost">
                  <USkeleton
                    v-for="i in 6"
                    :key="i"
                    class="h-[94px] rounded-lg mt-4 first:mt-0"
                  />
                </div>
                <div
                  v-if="posts.length !== 0"
                  class="flex items-center justify-center mt-10"
                >
                  <UPagination
                    v-model:page="paramsPost.page"
                    show-edges
                    :sibling-count="1"
                    :items-per-page="paramsPost.size"
                    :total="totalItemsPosts"
                  />
                </div>
                <LazyEmpty
                  v-if="!isLoadingPost && posts.length === 0"
                  :description="LL.no_results()"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="col-span-12 lg:col-span-3">
          <UCard>
            <h3 class="text-xl font-bold">{{ LL.people_you_may_know() }}</h3>
            <USeparator class="my-4" />
            <div class="flex flex-col gap-4">
              <div
                v-for="user in users?.data"
                :key="user.username"
                class="flex gap-2 items-center"
              >
                <UAvatar
                  :src="user.imageUrl ?? ''"
                  :alt="`${user.firstName} ${user.lastName}`"
                  size="md"
                  loading="lazy"
                />
                <div class="flex flex-col items-start">
                  <UButton
                    color="error"
                    class="font-semibold line-clamp-1"
                    variant="link"
                    :to="`/users/${user.username}`"
                    :padded="false"
                    >{{ user.firstName }} {{ user.lastName }}</UButton
                  >
                  <span class="text-xs text-neutral-400 dark:text-neutral-600">{{
                    user.username
                  }}</span>
                </div>
              </div>
            </div>
          </UCard>
        </div>
      </div>
    </UContainer>
  </div>
</template>
<script lang="ts" setup>
import { ref, reactive, watch } from "vue";
import { useRoute } from "vue-router";
import { typesafeI18n } from "~/locales/i18n-vue";
import { TPost, getPostsByUsername } from "~/features/post";
import { getInfoByUsername, getRandomUsers } from "~/features/user";
import { useUserStore } from "~/store";

definePageMeta({
  layout: "page",
});

const userStore = useUserStore();
const router = useRoute();

const { data: users } = await getRandomUsers();

const posts = ref<TPost[]>([]);

const params = reactive({
  page: 1,
  size: 6,
});

const paramsPost = reactive({
  page: 1,
  size: 6,
});

const { LL } = typesafeI18n();
const route = useRoute();

const isLoadingPost = ref(true);

const handleCurrentPageChange = (isFetchPost: boolean) => {
  if (isFetchPost) {
    fetchDataPost();
  }
};

watch(params, (_) => {
  handleCurrentPageChange(false);
});

watch(paramsPost, (_) => {
  handleCurrentPageChange(true);
  scrollToComponent("list-posts");
});

const userDetail = ref({
  id: "",
  follows: 0,
  username: "",
  firstName: "",
  imageUrl: "",
  lastName: "",
  isFollowed: false,
});
const totalItemsPosts = ref(0);

const scrollToComponent = (id: string) => {
  const element = document.getElementById(id);

  if (element) {
    element.scrollIntoView({
      behavior: "smooth",
    });
  }
};

const fetchDataPost = async () => {
  isLoadingPost.value = true;
  const { data: listPosts } = await getPostsByUsername(
    {
      page: paramsPost.page,
      size: paramsPost.size,
    },
    route.params.username as string
  );

  if (listPosts?.value) {
    posts.value = listPosts.value.items;
    totalItemsPosts.value = listPosts.value.total;
  }

  isLoadingPost.value = false;
};

const fetchDataInit = async () => {
  isLoadingPost.value = true;
  const [{ data: listPosts }, { data: userInfo }] = await Promise.all([
    getPostsByUsername(
      {
        page: paramsPost.page,
        size: paramsPost.size,
      },
      route.params.username as string
    ),
    getInfoByUsername(route.params.username as string),
  ]);

  if (listPosts?.value) {
    posts.value = listPosts.value.items;
    totalItemsPosts.value = listPosts.value.total;
  }
  if (userInfo?.value) {
    userDetail.value = userInfo.value;
  }
  isLoadingPost.value = false;
};

await fetchDataInit();

const runtime = useRuntimeConfig().public;

const { title, description, thumbnail, author } = useUserSeo(userDetail.value);
const ogImageUrl = computed(
  () =>
    `${runtime.host}/og-image?title=${encodeURIComponent(
      title.value
    )}&description=${encodeURIComponent(description.value)}`
);

const canonical = runtime.host + router.path;

useSeoMeta({
  title: title.value,
  description: description.value,
  author: author.value,

  ogImageUrl: ogImageUrl.value,
  twitterImage: ogImageUrl.value,
  twitterTitle: title.value,
});

useHead({
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@type": "Article",
        name: "KungFuTech",
        alternateName: "TechMely",
        url: canonical,
        "@context": "http://schema.org",
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": canonical,
        },
        headline: title.value,
        image: {
          "@type": "ImageObject",
          url: thumbnail.value,
          width: 700,
          height: 400,
        },
        author: {
          "@type": "Person",
          url: useAppConfig().author.link,
          name: author.value,
        },
        publisher: {
          "@type": "Organization",
          name: "TechMely",
          logo: {
            "@type": "ImageObject",
            url: thumbnail.value,
            width: 160,
            height: 160,
          },
        },
        description: description.value,
      }),
    },
  ],
});
</script>
